import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Clock, DollarSign, Info, TrendingUp, Users, Zap } from 'lucide-react';
import { SPREAD_RANGES, DEFAULT_SPREADS } from '../constants/spreadConfig';
import { SWAP_RATES_BY_TYPE } from '../constants/swapConfig';
import { CFD_TIERS } from '../constants/tradingTiers';

type FeesTab = 'spreads' | 'swaps' | 'leverage' | 'referral';

const TradingFeesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FeesTab>('spreads');

  const sampleSpreads = useMemo(() => Object.entries(DEFAULT_SPREADS).slice(0, 15), []);

  const referralTiers = [
    {
      name: 'Beginner',
      range: '0-2 referrals',
      badge: '0-2',
      commission: '1%',
      accentTextClass: 'text-slate-100',
      accentBadgeClass: 'bg-white/10 text-slate-100',
      accentLineClass: 'from-transparent via-white/60 to-transparent'
    },
    {
      name: 'Bronze',
      range: '3-9 referrals',
      badge: '3-9',
      commission: '1.5%',
      accentTextClass: 'text-indigo-100',
      accentBadgeClass: 'bg-indigo-400/15 text-indigo-100',
      accentLineClass: 'from-transparent via-indigo-300/80 to-transparent'
    },
    {
      name: 'Silver',
      range: '10-24 referrals',
      badge: '10-24',
      commission: '2%',
      accentTextClass: 'text-violet-100',
      accentBadgeClass: 'bg-violet-400/15 text-violet-100',
      accentLineClass: 'from-transparent via-violet-300/80 to-transparent'
    },
    {
      name: 'Gold',
      range: '25-49 referrals',
      badge: '25-49',
      commission: '3%',
      accentTextClass: 'text-fuchsia-100',
      accentBadgeClass: 'bg-fuchsia-400/15 text-fuchsia-100',
      accentLineClass: 'from-transparent via-fuchsia-300/80 to-transparent'
    },
    {
      name: 'Platinum',
      range: '50-99 referrals',
      badge: '50-99',
      commission: '4%',
      accentTextClass: 'text-purple-100',
      accentBadgeClass: 'bg-purple-400/15 text-purple-100',
      accentLineClass: 'from-transparent via-purple-300/80 to-transparent'
    },
    {
      name: 'Diamond',
      range: '100+ referrals',
      badge: 'Elite',
      commission: '5%',
      accentTextClass: 'text-pink-100',
      accentBadgeClass: 'bg-pink-400/15 text-pink-100',
      accentLineClass: 'from-transparent via-pink-300/80 to-transparent'
    }
  ];

  const homeBackgroundClass =
    'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_30%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.2),transparent_32%),radial-gradient(circle_at_bottom_center,rgba(217,70,239,0.14),transparent_38%)]';
  const primaryCardClass =
    'rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/24 via-indigo-500/18 to-purple-500/16 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
  const secondaryCardClass =
    'rounded-2xl border border-purple-500/35 bg-gradient-to-br from-blue-500/22 via-indigo-500/16 to-purple-500/18 shadow-2xl shadow-purple-500/15 backdrop-blur-sm';
  const innerCardClass =
    'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12';
  const innerCardHoverClass =
    'transition-all duration-300 hover:border-purple-400/45 hover:from-indigo-500/16 hover:via-purple-500/16 hover:to-fuchsia-500/16';
  const sectionIconClass =
    'flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25';
  const tabRailClass =
    'mb-8 flex flex-wrap gap-2 rounded-2xl border border-purple-500/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/10 to-fuchsia-500/12 p-2 backdrop-blur-sm';
  const activeTabClass =
    'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25';
  const inactiveTabClass =
    'border border-purple-500/20 bg-purple-950/35 text-slate-300 transition-all duration-300 hover:bg-purple-500/10 hover:border-purple-400/40 hover:text-white';
  const mutedTextClass = 'text-slate-400';
  const subtleTextClass = 'text-slate-300';
  const infoInsetClass = 'rounded-xl border border-purple-500/20 bg-slate-950/35';
  const warningCardClass =
    'rounded-xl border border-amber-400/25 bg-gradient-to-r from-amber-500/12 via-purple-500/10 to-fuchsia-500/12';

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className={`pointer-events-none absolute inset-0 ${homeBackgroundClass}`}></div>
      <div className="pointer-events-none absolute -left-24 top-8 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-3xl"></div>
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-indigo-500/12 blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-3 rounded-xl border border-purple-500/25 bg-purple-950/40 px-4 py-3 text-slate-200 transition-all duration-300 hover:border-purple-400/45 hover:bg-purple-500/12 hover:text-white"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/20">
            <ArrowLeft className="h-5 w-5 text-white" />
          </span>
          <span className="font-medium">Back</span>
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center rounded-full border border-purple-500/20 bg-purple-950/35 px-4 py-2 text-xs uppercase tracking-[0.28em] text-purple-200">
            Trading Conditions
          </div>
          <h1 className="mt-4 text-4xl font-bold text-white sm:text-5xl">
            Trading Fees{' '}
            <span className="bg-gradient-to-r from-indigo-100 via-violet-100 to-fuchsia-100 bg-clip-text text-transparent">
              & Conditions
            </span>
          </h1>
          <p className={`mt-3 max-w-3xl text-lg ${mutedTextClass}`}>
            Transparent pricing for every instrument, overnight cost, leverage tier, and referral reward on the
            platform.
          </p>
        </div>

        <div className={tabRailClass}>
          <button
            onClick={() => setActiveTab('spreads')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium ${activeTab === 'spreads' ? activeTabClass : inactiveTabClass}`}
          >
            <TrendingUp className="h-5 w-5" />
            Spreads
          </button>
          <button
            onClick={() => setActiveTab('swaps')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium ${activeTab === 'swaps' ? activeTabClass : inactiveTabClass}`}
          >
            <Clock className="h-5 w-5" />
            Swap Fees
          </button>
          <button
            onClick={() => setActiveTab('leverage')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium ${activeTab === 'leverage' ? activeTabClass : inactiveTabClass}`}
          >
            <Zap className="h-5 w-5" />
            Leverage Tiers
          </button>
          <button
            onClick={() => setActiveTab('referral')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium ${activeTab === 'referral' ? activeTabClass : inactiveTabClass}`}
          >
            <Users className="h-5 w-5" />
            Referral Program
          </button>
        </div>

        {activeTab === 'spreads' && (
          <div className="space-y-6">
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-start gap-4">
                <div className={sectionIconClass}>
                  <Info className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="mb-2 text-xl font-bold text-white">What are Spreads?</h2>
                  <p className={subtleTextClass}>
                    A spread is the difference between the buy and sell price of an instrument. It is charged when you
                    open a position and again when you close it, so tighter spreads mean lower trading costs.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(SPREAD_RANGES).map(([key, config]) => (
                <div key={key} className={`p-6 ${innerCardClass} ${innerCardHoverClass}`}>
                  <h3 className="mb-2 text-lg font-bold text-purple-100">{config.description}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className={mutedTextClass}>Range:</span>
                      <span className="font-mono text-white">{config.range}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className={mutedTextClass}>Typical:</span>
                      <span className="font-mono text-fuchsia-200">{config.typical}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-6 ${secondaryCardClass}`}>
              <h3 className="mb-4 text-xl font-bold text-white">Example Spreads</h3>
              <div className={`${infoInsetClass} overflow-x-auto`}>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className={`px-4 py-3 text-left font-medium ${mutedTextClass}`}>Instrument</th>
                      <th className={`px-4 py-3 text-left font-medium ${mutedTextClass}`}>Type</th>
                      <th className={`px-4 py-3 text-right font-medium ${mutedTextClass}`}>Spread %</th>
                      <th className={`px-4 py-3 text-right font-medium ${mutedTextClass}`}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleSpreads.map(([symbol, config]) => (
                      <tr key={symbol} className="border-b border-purple-500/10 transition-colors hover:bg-white/5">
                        <td className="px-4 py-3 font-medium text-white">{symbol}</td>
                        <td className={`px-4 py-3 capitalize ${mutedTextClass}`}>{config.instrumentType}</td>
                        <td className="px-4 py-3 text-right font-mono text-fuchsia-200">
                          {(config.spreadPercentage * 100).toFixed(4)}%
                        </td>
                        <td className={`px-4 py-3 text-right ${mutedTextClass}`}>{config.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={`mt-4 text-sm ${mutedTextClass}`}>
                Showing the top 15 instruments. Spreads are dynamic and may shift with live market conditions.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'swaps' && (
          <div className="space-y-6">
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-start gap-4">
                <div className={sectionIconClass}>
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="mb-2 text-xl font-bold text-white">What are Swap Fees?</h2>
                  <p className={`mb-4 ${subtleTextClass}`}>
                    Swap fees, also called overnight or rollover fees, are charged for holding leveraged positions past
                    the daily rollover window. They reflect the cost of financing your open exposure.
                  </p>
                  <div className={`p-4 ${warningCardClass}`}>
                    <p className="text-sm font-medium text-amber-200">
                      Important: swap fees are applied daily at 00:00 UTC for positions that remain open longer than 24
                      hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {Object.entries(SWAP_RATES_BY_TYPE).map(([key, config]) => (
                <div key={key} className={`p-6 ${innerCardClass} ${innerCardHoverClass}`}>
                  <div className="mb-4">
                    <h3 className="mb-1 text-lg font-bold text-purple-100">{config.description}</h3>
                    <p className={`text-sm ${mutedTextClass}`}>{config.notes}</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-t border-purple-500/20 py-2">
                      <span className={mutedTextClass}>Daily Rate:</span>
                      <span className="font-mono text-xl font-bold text-white">
                        {(config.dailySwapRate * 100).toFixed(4)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={mutedTextClass}>Annualized:</span>
                      <span className="font-mono text-fuchsia-200">
                        {(config.annualizedRate * 100).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-6 ${secondaryCardClass}`}>
              <h3 className="mb-4 text-xl font-bold text-white">Example: Swap Cost Calculation</h3>
              <div className={`p-6 ${infoInsetClass}`}>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <p className={`mb-2 ${mutedTextClass}`}>Position Details:</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="text-slate-500">Symbol:</span>
                        <span className="font-mono text-white">EUR/USD</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-500">Position Size:</span>
                        <span className="font-mono text-white">$10,000</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-500">Leverage:</span>
                        <span className="font-mono text-white">10x</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-500">Daily Swap Rate:</span>
                        <span className="font-mono text-purple-200">0.002%</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className={`mb-2 ${mutedTextClass}`}>Swap Costs:</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="text-slate-500">Per Day:</span>
                        <span className="font-mono text-rose-300">$0.20</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-500">Per Week (7 days):</span>
                        <span className="font-mono text-rose-300">$1.40</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-500">Per Month (30 days):</span>
                        <span className="font-mono text-rose-300">$6.00</span>
                      </li>
                      <li className="flex justify-between border-t border-purple-500/20 pt-2">
                        <span className="text-slate-500">Per Year (365 days):</span>
                        <span className="font-mono font-bold text-rose-300">$73.00</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leverage' && (
          <div className="space-y-6">
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-start gap-4">
                <div className={sectionIconClass}>
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="mb-2 text-xl font-bold text-white">Leverage Tiers</h2>
                  <p className={subtleTextClass}>
                    Available leverage increases with account equity. Higher tiers unlock more capital efficiency, but
                    they also demand tighter risk control.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {CFD_TIERS.map((tier, index) => (
                <div
                  key={tier.name}
                  className={`relative overflow-hidden p-6 ${innerCardClass} ${innerCardHoverClass}`}
                >
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-300/75 to-transparent"></div>
                  {index === CFD_TIERS.length - 1 && (
                    <div className="absolute right-4 top-4">
                      <Award className="h-8 w-8 text-fuchsia-200" />
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="mb-1 text-2xl font-bold text-purple-100">{tier.name}</h3>
                    <p className={`text-sm ${mutedTextClass}`}>{tier.description}</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-t border-purple-500/20 py-2">
                      <span className={mutedTextClass}>Min. Equity:</span>
                      <span className="font-mono text-lg font-bold text-white">${tier.minEquity.toLocaleString()}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className={`${infoInsetClass} p-3 text-center`}>
                        <p className="mb-1 text-xs text-slate-500">Forex</p>
                        <p className="font-mono text-lg font-bold text-fuchsia-200">{tier.maxForex}x</p>
                      </div>
                      <div className={`${infoInsetClass} p-3 text-center`}>
                        <p className="mb-1 text-xs text-slate-500">Commodities</p>
                        <p className="font-mono text-lg font-bold text-violet-200">{tier.maxCommodities}x</p>
                      </div>
                      <div className={`${infoInsetClass} p-3 text-center`}>
                        <p className="mb-1 text-xs text-slate-500">Stocks</p>
                        <p className="font-mono text-lg font-bold text-indigo-200">{tier.maxStocks}x</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-6 ${warningCardClass}`}>
              <h3 className="mb-3 text-lg font-bold text-amber-200">Risk Warning</h3>
              <p className="text-sm leading-relaxed text-slate-200">
                Trading with leverage can amplify both profits and losses. Use position sizing, stops, and disciplined
                risk management, and never trade with capital you cannot afford to lose.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'referral' && (
          <div className="space-y-6">
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-start gap-4">
                <div className={sectionIconClass}>
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="mb-2 text-xl font-bold text-white">Referral Commission Program</h2>
                  <p className={subtleTextClass}>
                    Earn recurring commission by referring active traders to the platform. As your network grows, your
                    payout tier increases automatically.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {referralTiers.map((tier) => (
                <div key={tier.name} className={`relative overflow-hidden p-6 ${innerCardClass} ${innerCardHoverClass}`}>
                  <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${tier.accentLineClass}`}></div>
                  <div className="mb-4 flex items-center gap-3">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 ${tier.accentBadgeClass}`}>
                      {tier.name === 'Diamond' ? (
                        <Award className="h-6 w-6" />
                      ) : (
                        <span className="text-xs font-bold">{tier.badge}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{tier.name}</h3>
                      <p className={`text-sm ${mutedTextClass}`}>{tier.range}</p>
                    </div>
                  </div>

                  <div className={`${infoInsetClass} p-4`}>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-4xl font-bold ${tier.accentTextClass}`}>{tier.commission}</span>
                      <span className={mutedTextClass}>commission</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-6 ${secondaryCardClass}`}>
              <h3 className="mb-4 text-xl font-bold text-white">How It Works</h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className={`${innerCardClass} p-6 text-center`}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/20">
                    <DollarSign className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="mb-2 font-bold text-white">Share Your Link</h4>
                  <p className={`text-sm ${mutedTextClass}`}>
                    Get your personal referral code and send it to new traders.
                  </p>
                </div>

                <div className={`${innerCardClass} p-6 text-center`}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/20">
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="mb-2 font-bold text-white">They Trade</h4>
                  <p className={`text-sm ${mutedTextClass}`}>
                    When your referrals trade profitably, your commission is calculated from their net gains.
                  </p>
                </div>

                <div className={`${innerCardClass} p-6 text-center`}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/20">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="mb-2 font-bold text-white">Get Paid</h4>
                  <p className={`text-sm ${mutedTextClass}`}>
                    Earnings are credited automatically, and your tier rises as you bring in more users.
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-6 ${warningCardClass}`}>
              <h3 className="mb-3 text-lg font-bold text-amber-200">Important Notes</h3>
              <ul className="space-y-3 text-sm text-slate-200">
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-200"></span>
                  <span>You earn commission only on your referrals&apos; profitable trades, not on losses.</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-200"></span>
                  <span>Commission is calculated on net profit after all trading costs, including spreads and swaps.</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-200"></span>
                  <span>Your tier upgrades automatically as your referral count grows from 1% up to 5%.</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-200"></span>
                  <span>No minimum payout threshold applies. Earnings are credited as soon as they are booked.</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingFeesPage;
